import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { startOfDay, endOfDay, isBefore, isAfter, addDays } from 'date-fns';

/**
 * Computes height-related metadata for a schedule event and
 * whether it should show "previous day" / "following day"
 * indicators for the current selected date.
 *
 * This hook keeps all time-span and multi-day calculations
 * outside of the view components.
 */
export const useEventHeight = (event) => {
  const selectedDate = useSelector((state) => state.schedule.selectedDate);

  const meta = useMemo(() => {
    if (!event || !event.extendedProps) {
      return {
        originalTimeHeight: 0,
        shouldRenderPrevDayBlock: false,
        shouldRenderNextDayBlock: false,
      };
    }

    const { originalStart, originalEnd } = event.extendedProps;

    if (!originalStart || !originalEnd || !selectedDate) {
      return {
        originalTimeHeight: 0,
        shouldRenderPrevDayBlock: false,
        shouldRenderNextDayBlock: false,
      };
    }

    let start;
    let end;

    try {
      start = new Date(originalStart);
      end = new Date(originalEnd);
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error('Invalid dates');
      }
    } catch {
      return {
        originalTimeHeight: 0,
        shouldRenderPrevDayBlock: false,
        shouldRenderNextDayBlock: false,
      };
    }

    const dayStart = startOfDay(new Date(selectedDate));
    const dayEnd = endOfDay(new Date(selectedDate));
    const nextDayStart = startOfDay(addDays(new Date(selectedDate), 1));

    // Clamp original start/end to the visible day to reproduce
    // legacy "originalTimeHeight" behaviour.
    let clampedStartTime = start.getTime();
    let clampedEndTime = end.getTime();

    if (isBefore(start, dayStart)) {
      clampedStartTime = dayStart.getTime();
    }
    if (isAfter(end, dayEnd)) {
      clampedEndTime = dayEnd.getTime();
    }

    const diffMinutes = (clampedEndTime - clampedStartTime) / 1000 / 60;
    const originalTimeHeight = Math.max(0, Math.floor((diffMinutes * 2) / 3));

    const shouldRenderPrevDayBlock = isBefore(start, dayStart);
    const isEndOverflowCalendar =
      !isAfter(end, nextDayStart) && !isBefore(end, dayEnd);
    const isEndOnNextDay =
      !isAfter(startOfDay(end), nextDayStart) && isAfter(startOfDay(end), dayEnd);
    const shouldRenderNextDayBlock = isEndOverflowCalendar || isEndOnNextDay;

    return {
      originalTimeHeight,
      shouldRenderPrevDayBlock,
      shouldRenderNextDayBlock,
    };
  }, [event, selectedDate]);

  return meta;
};

export default useEventHeight;

